import { useQuery } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import { z } from 'zod';
import { logger } from '@/lib/logger'

// Balance response schema
const BalanceSchema = z.object({
  owes: z.number(),
  owed: z.number(),
});

const BalancesResponseSchema = z.object({
  balances: BalanceSchema,
  allBalances: z.record(z.string(), BalanceSchema).optional(),
  totalExpenses: z.number(),
  expenseCount: z.number(),
});

export type UserBalance = z.infer<typeof BalanceSchema>;
export type BalancesResponse = z.infer<typeof BalancesResponseSchema>;

export function useGroupBalances(groupId: string) {
  logger.debug('useGroupBalances called with groupId:', { groupId });
  return useQuery({
    queryKey: [QUERY_KEYS.GROUP_BALANCES, groupId],
    queryFn: async () => {
      logger.debug('Balance query function executing for group:', { groupId });
      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          const url = `/groups/${groupId}/balances`;
          logger.debug('Fetching balances from:', { url });

          const data = yield* apiClient.get(url, BalancesResponseSchema);
          logger.debug('Balance API response:', { data });
          return data;
        }),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    enabled: !!groupId,
    staleTime: 30_000, // 30 seconds
  });
}